// utils/exportUtils.js
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ============================================
// Shared Helpers
// ============================================

// Marker used wherever a mandatory roll number / registration number is absent,
// so gaps are obvious in result & attempt reports instead of silently blank.
export const ROLL_NUMBER_MISSING = 'MISSING';

/**
 * Normalise a roll number (registration number) for reports.
 * Roll number is a mandatory identifying field — when it is absent we surface
 * an explicit MISSING marker rather than 'N/A' or an empty cell.
 * @param {*} value Raw registration number
 * @returns {string} The roll number, or ROLL_NUMBER_MISSING when absent
 */
export const formatRollNumber = (value) => {
  const str = value === null || value === undefined ? '' : String(value).trim();
  return str === '' ? ROLL_NUMBER_MISSING : str;
};

/**
 * Build a dynamic report filename that encodes the applied filters, so a
 * downloaded file describes what it actually contains.
 *
 * buildReportFilename('student_progress', { degree: 'BTECH', batch: '2024-2027', progressLevel: 'high' }, 'csv')
 *   => 'student_progress_degree-BTECH_batch-2024-2027_progressLevel-high_2026-06-11.csv'
 *
 * @param {string} baseName  Base report name (e.g. 'student_progress')
 * @param {Object} [filters] Applied filters; non-empty values become name-value segments
 * @param {string} [extension] File extension without the dot (default 'xlsx')
 * @returns {string} Sanitised filename ending in `.${extension}`
 */
export const buildReportFilename = (baseName, filters = {}, extension = 'xlsx') => {
  const sanitize = (value) =>
    String(value)
      .trim()
      .replace(/[^a-z0-9]+/gi, '-')
      .replace(/^-+|-+$/g, '');

  // Values we treat as "no filter applied"
  const isEmpty = (value) =>
    value === null || value === undefined || value === '' || value === 'all';

  // Keys that describe pagination/sorting rather than the data set itself
  const ignoredKeys = new Set([
    'page', 'limit', 'offset', 'sort', 'sortBy', 'sortOrder', 'order'
  ]);

  const segments = [sanitize(baseName)];

  Object.entries(filters || {}).forEach(([key, value]) => {
    if (ignoredKeys.has(key) || isEmpty(value)) return;
    segments.push(`${sanitize(key)}-${sanitize(value)}`);
  });

  // Always append the generation date for traceability
  segments.push(new Date().toISOString().split('T')[0]);

  return `${segments.filter(Boolean).join('_')}.${extension}`;
};

// ============================================
// Excel Export Functions
// ============================================

export const generateExcelReport = (analyticsData) => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // ===== Overview Sheet =====
    const overviewData = [
      ['Metric', 'Value'],
      ['', ''],
      ['=== ACTIVE USERS ===', ''],
      ['Daily Active Users', analyticsData.overview.activeUsers.dailyActiveUsers],
      ['Weekly Active Users', analyticsData.overview.activeUsers.weeklyActiveUsers],
      ['Monthly Active Users', analyticsData.overview.activeUsers.monthlyActiveUsers],
      ['Average Session Duration (min)', analyticsData.overview.averageSessionDuration],
      ['', ''],
      ['=== ENROLLMENTS ===', ''],
      ['Total Enrollments', analyticsData.overview.enrollmentStats.totalEnrollments],
      ['Active Enrollments', analyticsData.overview.enrollmentStats.activeEnrollments],
      ['Completed Enrollments', analyticsData.overview.enrollmentStats.completedEnrollments],
      ['Dropped Enrollments', analyticsData.overview.enrollmentStats.droppedEnrollments],
      ['Completion Rate (%)', parseFloat(analyticsData.overview.enrollmentStats.completionRate)],
      ['', ''],
      ['=== EXAMS ===', ''],
      ['Total Exams', analyticsData.overview.examStats.totalExams],
      ['Total Attempts', analyticsData.overview.examStats.totalAttempts],
      ['Completed Attempts', analyticsData.overview.examStats.completedAttempts],
      ['Passed Attempts', analyticsData.overview.examStats.passedAttempts],
      ['Pass Rate (%)', parseFloat(analyticsData.overview.examStats.passRate)],
      ['Average Score (%)', parseFloat(analyticsData.overview.examStats.averageScore)],
      ['', ''],
      ['=== CERTIFICATES ===', ''],
      ['Total Issued', analyticsData.overview.certificateStats.totalIssued],
      ['Approved', analyticsData.overview.certificateStats.approved],
      ['Pending', analyticsData.overview.certificateStats.pending],
      ['Revoked', analyticsData.overview.certificateStats.revoked],
      ['Approval Rate (%)', parseFloat(analyticsData.overview.certificateStats.approvalRate)]
    ];

    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    
    // Set column widths
    overviewSheet['!cols'] = [
      { wch: 35 },
      { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

    // ===== Monthly Growth Sheet =====
    const monthlyGrowthData = [
      ['Month', 'Students', 'Certificates', 'Courses'],
      ...analyticsData.trends.monthlyGrowth.map(month => [
        month.month,
        month.students,
        month.certificates,
        month.courses
      ])
    ];

    const monthlyGrowthSheet = XLSX.utils.aoa_to_sheet(monthlyGrowthData);
    
    monthlyGrowthSheet['!cols'] = [
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(workbook, monthlyGrowthSheet, 'Monthly Growth');

    // ===== Top Colleges Sheet =====
    const topCollegesData = [
      ['Rank', 'College Name', 'Code', 'Students', 'Certificates', 'Completion Rate (%)'],
      ...analyticsData.performance.topColleges.map((college, index) => [
        index + 1,
        college.name,
        college.code || '',
        college.students,
        college.certificates,
        college.completion
      ])
    ];

    const topCollegesSheet = XLSX.utils.aoa_to_sheet(topCollegesData);
    
    topCollegesSheet['!cols'] = [
      { wch: 10 },
      { wch: 35 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(workbook, topCollegesSheet, 'Top Colleges');

    // ===== Popular Courses Sheet =====
    const popularCoursesData = [
      ['Rank', 'Course Name', 'Course Code', 'Enrollments'],
      ...analyticsData.performance.popularCourses.map((course, index) => [
        index + 1,
        course.name,
        course.code,
        course.enrollments
      ])
    ];

    const popularCoursesSheet = XLSX.utils.aoa_to_sheet(popularCoursesData);
    
    popularCoursesSheet['!cols'] = [
      { wch: 10 },
      { wch: 45 },
      { wch: 15 },
      { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(workbook, popularCoursesSheet, 'Popular Courses');

    // ===== Subject Completion Rates Sheet =====
    const subjectCompletionData = [
      ['Subject', 'Completion Rate (%)'],
      ...Object.entries(analyticsData.performance.completionRates).map(([subject, rate]) => [
        subject,
        rate
      ])
    ];

    const subjectCompletionSheet = XLSX.utils.aoa_to_sheet(subjectCompletionData);
    
    subjectCompletionSheet['!cols'] = [
      { wch: 35 },
      { wch: 20 }
    ];

    XLSX.utils.book_append_sheet(workbook, subjectCompletionSheet, 'Subject Completion');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Download file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = buildReportFilename('analytics_report', {}, 'xlsx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, message: 'Excel file downloaded successfully' };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// Exam Attempts Export Function
// ============================================

export const exportExamAttemptsToExcel = (examTitle, examAttempts, attemptStats, totalMarks, degree) => {
  try {
    const workbook = XLSX.utils.book_new();

    // Roll Number (registration number) is a mandatory identifying field.
    // Count any attempts missing one so the gap is reported up front.
    const missingRollNumbers = examAttempts.filter(
      (attempt) => formatRollNumber(attempt.students?.registration_no) === ROLL_NUMBER_MISSING
    ).length;

    // ===== Summary Sheet =====
    const summaryData = [
      ['Exam Attempts Report'],
      ['Exam Title:', examTitle],
      ['Generated On:', new Date().toLocaleString('en-IN')],
      [''],
      ['=== STATISTICS ==='],
      ['Total Attempts', attemptStats?.total_attempts || 0],
      ['Completed Attempts', attemptStats?.completed || 0],
      ['In Progress', attemptStats?.in_progress || 0],
      ['Passed', attemptStats?.passed || 0],
      ['Failed', attemptStats?.failed || 0],
      ['Pass Rate (%)', attemptStats?.completed > 0 ? Math.round((attemptStats.passed / attemptStats.completed) * 100) : 0],
      ['Average Score', attemptStats?.average_score?.toFixed(2) || 'N/A'],
      ['Total Marks', totalMarks || 100],
      ['Missing Roll Numbers', missingRollNumbers],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 22 }, { wch: 30 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // ===== Attempts Details Sheet =====
    // Roll Number is the leading column; missing values are flagged, not blanked.
    const attemptsData = [
      [
        'Roll Number',
        'Student Name',
        "Father's Name",
        'Started At',
        'Completed At',
        'Duration (minutes)',
        'Score',
        'Total Marks',
        'Percentage (%)',
        'Status',
        'Result'
      ]
    ];

    examAttempts.forEach((attempt) => {
      const startTime = attempt.started_at ? new Date(attempt.started_at) : null;
      const endTime = attempt.completed_at ? new Date(attempt.completed_at) : null;
      const duration = startTime && endTime
        ? Math.round((endTime - startTime) / 60000)
        : 'N/A';

      const rollNumber = formatRollNumber(attempt.students?.registration_no);

      attemptsData.push([
        rollNumber,
        attempt.students?.candidate_name || 'N/A',
        attempt.students?.father_name || 'N/A',
        startTime ? startTime.toLocaleString('en-IN') : 'N/A',
        endTime ? endTime.toLocaleString('en-IN') : 'In Progress',
        duration,
        attempt.score !== null ? attempt.score : 'N/A',
        attempt.total_marks || totalMarks,
        attempt.percentage !== null ? parseFloat(attempt.percentage).toFixed(2) : 'N/A',
        attempt.completed_at ? 'Completed' : 'In Progress',
        attempt.completed_at
          ? (attempt.is_passed ? 'Passed' : 'Failed')
          : 'N/A'
      ]);
    });

    const attemptsSheet = XLSX.utils.aoa_to_sheet(attemptsData);
    attemptsSheet['!cols'] = [
      { wch: 18 },  // Roll Number
      { wch: 25 },  // Student Name
      { wch: 22 },  // Father's Name
      { wch: 20 },  // Started At
      { wch: 20 },  // Completed At
      { wch: 18 },  // Duration
      { wch: 10 },  // Score
      { wch: 12 },  // Total Marks
      { wch: 15 },  // Percentage
      { wch: 12 },  // Status
      { wch: 10 }   // Result
    ];
    XLSX.utils.book_append_sheet(workbook, attemptsSheet, 'Attempts Details');

    // ===== Performance Analysis Sheet =====
    if (attemptStats && attemptStats.completed > 0) {
      const performanceData = [
        ['Performance Analysis'],
        [''],
        ['Category', 'Count', 'Percentage'],
        ['Passed', attemptStats.passed, `${Math.round((attemptStats.passed / attemptStats.completed) * 100)}%`],
        ['Failed', attemptStats.failed, `${Math.round((attemptStats.failed / attemptStats.completed) * 100)}%`],
        [''],
        ['Completion Status', 'Count', 'Percentage'],
        ['Completed', attemptStats.completed, `${Math.round((attemptStats.completed / attemptStats.total_attempts) * 100)}%`],
        ['In Progress', attemptStats.in_progress, `${Math.round((attemptStats.in_progress / attemptStats.total_attempts) * 100)}%`],
      ];

      const performanceSheet = XLSX.utils.aoa_to_sheet(performanceData);
      performanceSheet['!cols'] = [{ wch: 20 }, { wch: 15 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(workbook, performanceSheet, 'Performance Analysis');
    }

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });

    // Download file
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = buildReportFilename(`${examTitle}_attempts`, { ...(degree && { degree }) }, 'xlsx');
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, message: 'Excel file downloaded successfully' };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// Exam Live Analytics Export Function
// ============================================

export const exportExamAnalyticsToExcel = (analytics) => {
  try {
    if (!analytics) {
      throw new Error('No analytics data available');
    }

    const exam = analytics.exam || {};
    const summary = analytics.summary || {};
    const students = analytics.students || {};
    const examTitle = exam.title || 'Exam';
    const courseLabel = typeof exam.course === 'object' && exam.course !== null
      ? [exam.course.title, exam.course.course_code].filter(Boolean).join(' - ')
      : (exam.course || 'N/A');

    const formatDateTime = (value) =>
      value ? new Date(value).toLocaleString('en-IN') : 'N/A';

    const formatRemaining = (seconds) => {
      if (seconds === null || seconds === undefined) return 'N/A';
      const total = Math.max(0, Math.floor(seconds));
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      return `${h}h ${m}m ${s}s`;
    };

    const workbook = XLSX.utils.book_new();

    // ===== Summary Sheet =====
    const summaryData = [
      ['Exam Live Analytics Report'],
      ['Exam Title:', examTitle],
      ['Course:', courseLabel],
      ['Window Start:', formatDateTime(exam.start_time)],
      ['Window End:', formatDateTime(exam.end_time)],
      ['Generated On:', formatDateTime(analytics.generated_at) === 'N/A'
        ? new Date().toLocaleString('en-IN')
        : formatDateTime(analytics.generated_at)],
      [''],
      ['=== PARTICIPATION ==='],
      ['Total Eligible', summary.total_eligible ?? 0],
      ['Not Started', summary.not_started ?? 0],
      ['In Progress', summary.in_progress ?? 0],
      ['Completed', summary.completed ?? 0],
      [''],
      ['=== RESULTS (completed only) ==='],
      ['Passed', summary.passed ?? 0],
      ['Failed', summary.failed ?? 0],
      ['Pass Percentage (%)', summary.pass_percentage ?? 0],
      ['Average Score', summary.avg_score ?? 0],
      ['Highest Score', summary.highest_score ?? 0],
      ['Lowest Score', summary.lowest_score ?? 0],
    ];

    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 24 }, { wch: 32 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Roll Number (registration number) is a mandatory identifying field in
    // result reports and therefore leads every student sheet below.
    // ===== Completed Sheet =====
    const completedData = [
      ['Roll Number', 'Name', 'Email', 'Batch', 'Score', 'Percentage (%)', 'Result', 'Completed At'],
      ...(students.completed || []).map((s) => [
        formatRollNumber(s.registration_no),
        s.name || 'N/A',
        s.email || 'N/A',
        s.batch || 'N/A',
        s.score ?? 'N/A',
        s.percentage !== null && s.percentage !== undefined
          ? parseFloat(s.percentage).toFixed(2)
          : 'N/A',
        s.is_passed ? 'Passed' : 'Failed',
        formatDateTime(s.completed_at),
      ]),
    ];
    const completedSheet = XLSX.utils.aoa_to_sheet(completedData);
    completedSheet['!cols'] = [
      { wch: 18 }, { wch: 25 }, { wch: 28 }, { wch: 14 },
      { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 22 },
    ];
    XLSX.utils.book_append_sheet(workbook, completedSheet, 'Completed');

    // ===== In Progress Sheet =====
    const inProgressData = [
      ['Roll Number', 'Name', 'Email', 'Batch', 'Started At', 'Time Remaining'],
      ...(students.in_progress || []).map((s) => [
        formatRollNumber(s.registration_no),
        s.name || 'N/A',
        s.email || 'N/A',
        s.batch || 'N/A',
        formatDateTime(s.started_at),
        formatRemaining(s.time_remaining_seconds),
      ]),
    ];
    const inProgressSheet = XLSX.utils.aoa_to_sheet(inProgressData);
    inProgressSheet['!cols'] = [
      { wch: 18 }, { wch: 25 }, { wch: 28 }, { wch: 14 }, { wch: 22 }, { wch: 16 },
    ];
    XLSX.utils.book_append_sheet(workbook, inProgressSheet, 'In Progress');

    // ===== Not Started Sheet =====
    const notStartedData = [
      ['Roll Number', 'Name', 'Email', 'Batch'],
      ...(students.not_started || []).map((s) => [
        formatRollNumber(s.registration_no),
        s.name || 'N/A',
        s.email || 'N/A',
        s.batch || 'N/A',
      ]),
    ];
    const notStartedSheet = XLSX.utils.aoa_to_sheet(notStartedData);
    notStartedSheet['!cols'] = [{ wch: 18 }, { wch: 25 }, { wch: 28 }, { wch: 14 }];
    XLSX.utils.book_append_sheet(workbook, notStartedSheet, 'Not Started');

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fileName = buildReportFilename(`${examTitle}_analytics`, { ...(exam.degree_code && { degree: exam.degree_code }) }, 'xlsx');
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true, message: 'Excel file downloaded successfully' };
  } catch (error) {
    console.error('Excel export error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// PDF Export Functions
// ============================================

export const generatePDFReport = (analyticsData) => {
  try {
    const doc = new jsPDF();
    let yPosition = 20;

    // Helper function to add text with word wrap
    const addText = (text, x, y, options = {}) => {
      const { fontSize = 11, fontStyle = 'normal', maxWidth = 180 } = options;
      doc.setFontSize(fontSize);
      doc.setFont('helvetica', fontStyle);
      
      if (maxWidth) {
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.5);
      } else {
        doc.text(text, x, y);
        return fontSize * 0.5;
      }
    };

    // ===== Title =====
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('System Analytics Report', 105, yPosition, { align: 'center' });
    yPosition += 10;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Gyan Sathi Learning Management System', 105, yPosition, { align: 'center' });
    yPosition += 6;

    doc.setFontSize(10);
    doc.setTextColor(128, 128, 128);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 105, yPosition, { align: 'center' });
    yPosition += 15;

    doc.setTextColor(0, 0, 0);

    // ===== Overview Section =====
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Active Users Statistics', 20, yPosition);
    yPosition += 8;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);

    const activeUsersData = [
      ['Daily Active Users', analyticsData.overview.activeUsers.dailyActiveUsers.toLocaleString()],
      ['Weekly Active Users', analyticsData.overview.activeUsers.weeklyActiveUsers.toLocaleString()],
      ['Monthly Active Users', analyticsData.overview.activeUsers.monthlyActiveUsers.toLocaleString()],
      ['Average Session Duration', `${analyticsData.overview.averageSessionDuration} minutes`]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: activeUsersData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // ===== Enrollment Statistics =====
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Enrollment Statistics', 20, yPosition);
    yPosition += 8;

    const enrollmentData = [
      ['Total Enrollments', analyticsData.overview.enrollmentStats.totalEnrollments.toLocaleString()],
      ['Active Enrollments', analyticsData.overview.enrollmentStats.activeEnrollments.toLocaleString()],
      ['Completed Enrollments', analyticsData.overview.enrollmentStats.completedEnrollments.toLocaleString()],
      ['Dropped Enrollments', analyticsData.overview.enrollmentStats.droppedEnrollments.toLocaleString()],
      ['Completion Rate', `${analyticsData.overview.enrollmentStats.completionRate}%`]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: enrollmentData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // ===== Exam Statistics =====
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Exam Statistics', 20, yPosition);
    yPosition += 8;

    const examData = [
      ['Total Exams', analyticsData.overview.examStats.totalExams.toLocaleString()],
      ['Total Attempts', analyticsData.overview.examStats.totalAttempts.toLocaleString()],
      ['Completed Attempts', analyticsData.overview.examStats.completedAttempts.toLocaleString()],
      ['Passed Attempts', analyticsData.overview.examStats.passedAttempts.toLocaleString()],
      ['Pass Rate', `${analyticsData.overview.examStats.passRate}%`],
      ['Average Score', `${analyticsData.overview.examStats.averageScore}%`]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: examData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // ===== Certificate Statistics =====
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Certificate Statistics', 20, yPosition);
    yPosition += 8;

    const certificateData = [
      ['Total Issued', analyticsData.overview.certificateStats.totalIssued.toLocaleString()],
      ['Approved', analyticsData.overview.certificateStats.approved.toLocaleString()],
      ['Pending', analyticsData.overview.certificateStats.pending.toLocaleString()],
      ['Revoked', analyticsData.overview.certificateStats.revoked.toLocaleString()],
      ['Approval Rate', `${analyticsData.overview.certificateStats.approvalRate}%`]
    ];

    doc.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: certificateData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    // ===== New Page for Charts =====
    doc.addPage();
    yPosition = 20;

    // ===== Top Performing Colleges =====
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Top Performing Colleges', 20, yPosition);
    yPosition += 8;

    const collegeData = analyticsData.performance.topColleges.map((college, index) => [
      index + 1,
      college.name,
      college.students,
      college.certificates,
      `${college.completion}%`
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Rank', 'College Name', 'Students', 'Certificates', 'Completion']],
      body: collegeData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // ===== Most Popular Courses =====
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Most Popular Courses', 20, yPosition);
    yPosition += 8;

    const courseData = analyticsData.performance.popularCourses.slice(0, 10).map((course, index) => [
      index + 1,
      course.name,
      course.code,
      course.enrollments
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Rank', 'Course Name', 'Code', 'Enrollments']],
      body: courseData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 },
      columnStyles: {
        1: { cellWidth: 80 }
      }
    });

    yPosition = doc.lastAutoTable.finalY + 10;

    // ===== Subject Completion Rates =====
    if (yPosition > 200) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text('Subject-wise Completion Rates', 20, yPosition);
    yPosition += 8;

    const subjectData = Object.entries(analyticsData.performance.completionRates).map(([subject, rate]) => [
      subject,
      `${rate}%`
    ]);

    doc.autoTable({
      startY: yPosition,
      head: [['Subject', 'Completion Rate']],
      body: subjectData,
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
      margin: { left: 20, right: 20 }
    });

    // ===== Footer on all pages =====
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: 'center' });
      doc.text('© Gyan Sathi LMS - Confidential', 105, 290, { align: 'center' });
    }

    // Save PDF
    doc.save(buildReportFilename('analytics_report', {}, 'pdf'));

    return { success: true, message: 'PDF file downloaded successfully' };
  } catch (error) {
    console.error('PDF export error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// Export Handler Function
// ============================================

export const exportAnalytics = async (analyticsData, format) => {
  try {
    if (!analyticsData) {
      throw new Error('No analytics data available');
    }

    if (format === 'excel') {
      return generateExcelReport(analyticsData);
    } else if (format === 'pdf') {
      return generatePDFReport(analyticsData);
    } else {
      throw new Error('Invalid format. Use "excel" or "pdf"');
    }
  } catch (error) {
    console.error('Export error:', error);
    return { success: false, error: error.message };
  }
};